import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import {
  PartnerVerificationFormValues,
  PartnerVerificationFormViolation,
  TUserAccountInfo,
} from 'types/state';
import { validatePartnerVerificationFormValues } from 'utils/validatePartnerVerificationFormValues';
import { useServices } from 'hooks/useServices';
import { PromocodeNotFoundError } from 'utils/errors';
import { CodeInputLayoutComponent } from 'design/codeInputLayout';
import { CharInputs } from 'containers/CharInputs';
import { SpinnerComponent } from 'design/spinner';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';
import { useUserRoles } from 'hooks/useUserRole';

const initialFormValues: PartnerVerificationFormValues = {
  codeChars: ['', '', '', '', '', ''],
};

type Props = {
  onSubmitDidSucceed(accountInfo: void | TUserAccountInfo): void;
};

export const PartnerRegistrationManagerVerificationForm = (props: Props) => {
  const { onSubmitDidSucceed } = props;

  const { verifyPartner } = useServices();

  const isClient = useUserRoles().includes('CLIENT');

  const intl = useIntlDictionary();

  const {
    partnerRegistrationManager: { verificationForm: ownDictionary },
  } = intl;

  const [formValues, setFormValues] = React.useState<
    PartnerVerificationFormValues
  >(initialFormValues);

  const [formViolations, setFormViolations] = React.useState<
    PartnerVerificationFormViolation[]
  >([]);

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  React.useEffect(() => {
    const violations = validatePartnerVerificationFormValues(formValues);

    if (violations.length === 0) {
      const promise = verifyPartner({ values: formValues, isClient })
        .then(onSubmitDidSucceed)
        .catch((error) => {
          if (error instanceof PromocodeNotFoundError) {
            setFormValues(initialFormValues);

            setFormViolations([{ name: 'InvalidCode' }]);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [formValues, isClient, injectPromise, onSubmitDidSucceed, verifyPartner]);

  const setCodeValue = React.useCallback((nextValue: string[]) => {
    setFormViolations([]);

    setFormValues((prev) => ({
      ...prev,
      codeChars: nextValue,
    }));
  }, []);

  const invalidCharIndexes = React.useMemo(() => {
    if (formViolations.length > 0) {
      return [0, 1, 2, 3, 4, 5];
    }
  }, [formViolations.length]);

  const hasViolatedField = formViolations.length > 0;

  const codeIsInvalid = formViolations.some(
    (violation) => violation.name === 'InvalidCode'
  );

  return (
    <FormControlComponent shouldFocusFirstInvalidInput={hasViolatedField}>
      <ContentLayoutComponent
        title={
          <TextComponent color="primary" size="l" family="futura">
            {ownDictionary.titleText}
          </TextComponent>
        }
        body={
          <>
            {codeIsInvalid ? (
              <TextComponent color="critical">
                {ownDictionary.invalidCodeDescriptionText}
              </TextComponent>
            ) : (
              <TextComponent color="primary">
                {ownDictionary.descriptionText}
              </TextComponent>
            )}
            <MarginComponent top={28} />
            <CodeInputLayoutComponent
              theme="secretCode"
              inputs={
                <CharInputs
                  name="secretCode"
                  chars={formValues.codeChars}
                  onCharsChange={setCodeValue}
                  invalidCharIndexes={invalidCharIndexes}
                  disabled={asyncActive}
                  autoFocus
                />
              }
              indicator={
                asyncActive && (
                  <TextComponent color="accent">
                    <SpinnerComponent />
                  </TextComponent>
                )
              }
            />
          </>
        }
        backAction={null}
      />
    </FormControlComponent>
  );
};
